import { useEffect, useState, type JSX } from 'react'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import classNames from 'classnames'
import { useResponsive } from '@dustin-web/microsite-components/src/hooks'
import { ProductReferenceFragment } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import type { Props as HeadingProps } from '@dustin-web/microsite-components/src/heading'
import type { DigitalDataOrigin } from '../utils/digital-data-types'
import type { ProductCardType } from './use-product-list-settings'

import { ListItemRenderer } from './product-list'
import TakebackWrapper from '../services-banners/takeback-wrapper'

const MOBILE_LAYOUT = {
  gap: 8,
  itemSize: 295,
  paddingEnd: 8,
  initialRect: { width: 375, height: 900 },
}

const DESKTOP_LAYOUT = {
  gap: 16,
  itemSize: 250,
  paddingEnd: 16,
  initialRect: { width: 1920, height: 1580 },
}

type Props = {
  products: ProductReferenceFragment[]
  promotedProductCard: JSX.Element | null
  showSideButtons: boolean
  viewType: ProductCardType | string
  infinityScroll: boolean
  pageSize: number
  hTag: HeadingProps['hTag'] | undefined
  digitalDataOrigin: DigitalDataOrigin
  showTakebackBanner?: boolean
  categoryId?: string | undefined
}

const ProductVirtualList = ({
  products,
  promotedProductCard,
  showSideButtons,
  viewType,
  infinityScroll,
  digitalDataOrigin,
  hTag,
  pageSize,
  showTakebackBanner,
  categoryId,
}: Props) => {
  const [prevLastIndex, setPrevLastIndex] = useState<number>(0)
  const { isSmallScreen } = useResponsive()

  const { gap, paddingEnd, itemSize, initialRect } = isSmallScreen ? MOBILE_LAYOUT : DESKTOP_LAYOUT
  const { getVirtualItems, getTotalSize, measure } = useWindowVirtualizer({
    estimateSize: () => itemSize,
    count: products.length,
    overscan: 5,
    gap,
    paddingEnd,
    initialRect,
  })

  useEffect(() => {
    measure()
  }, [isSmallScreen, measure])

  const virtualItems = getVirtualItems()
  const currentLastIndex = virtualItems[virtualItems.length - 1]?.index ?? null

  useEffect(() => {
    if (currentLastIndex !== null && currentLastIndex > prevLastIndex)
      setPrevLastIndex(currentLastIndex)
  }, [currentLastIndex])

  const bannerIndex = showTakebackBanner ? products.findIndex(p => p.id === 'takebackBanner') : -1

  return (
    <div
      className="row sm-gutters"
      style={{
        height: `${getTotalSize()}px`,
        position: 'relative',
      }}>
      {promotedProductCard && <>{promotedProductCard}</>}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          transform: `translateY(${getVirtualItems()[0]?.start ?? 0}px)`,
        }}>
        {getVirtualItems().map(({ index, key }) => {
          if (showTakebackBanner && products[index].id === 'takebackBanner') {
            return (
              <div
                key={key}
                className={classNames('product-virtual-wrapper', { mobile: isSmallScreen })}>
                <TakebackWrapper listType={viewType} categoryId={categoryId} />
              </div>
            )
          }
          const productIndex =
            showTakebackBanner && bannerIndex !== -1 && index > bannerIndex ? index - 1 : index
          return (
            products[index].searchResultProduct && (
              <div
                key={key}
                className={classNames('product-virtual-wrapper', { mobile: isSmallScreen })}>
                <ListItemRenderer
                  inFilterList
                  imagePriority
                  product={products[index].searchResultProduct!}
                  showSideButtons={showSideButtons}
                  ticket={products[index].ticket}
                  listType={viewType}
                  isExtraItem={infinityScroll ? false : pageSize ? productIndex >= pageSize : false}
                  digitalDataOrigin={digitalDataOrigin}
                  digitalIndex={productIndex}
                  hTag={hTag}
                  prevLastIndex={prevLastIndex}
                />
              </div>
            )
          )
        })}
      </div>
    </div>
  )
}

export default ProductVirtualList
